import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Bulk Import Users modal.
 * Accepts either:
 *   - a JSON array:  [{...}, {...}]
 *   - multiple JSON objects separated by blank lines (what the owner pasted in chat)
 * Passwords must already be bcrypt hashes ($2a$/$2b$/$2y$).
 */
export const BulkImportUsersModal = ({ isOpen, onClose, api, onImportComplete }) => {
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const parseInput = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return [];
    // Try as JSON array first
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'object' && parsed !== null) return [parsed];
    } catch {
      // fall through to multi-object parsing
    }
    // Split on "}\s*\n\s*{" (multiple JSON objects pasted sequentially)
    const objects = [];
    let depth = 0;
    let start = -1;
    for (let i = 0; i < trimmed.length; i++) {
      const ch = trimmed[i];
      if (ch === '{') {
        if (depth === 0) start = i;
        depth++;
      } else if (ch === '}') {
        depth--;
        if (depth === 0 && start !== -1) {
          const chunk = trimmed.slice(start, i + 1);
          try {
            objects.push(JSON.parse(chunk));
          } catch (e) {
            throw new Error(`Failed to parse object at position ${start}: ${e.message}`);
          }
          start = -1;
        }
      }
    }
    return objects;
  };

  const handleImport = async () => {
    let users;
    try {
      users = parseInput(raw);
    } catch (e) {
      toast.error(`Parse error: ${e.message}`);
      return;
    }
    if (!users.length) {
      toast.error('No user objects found. Paste one or more JSON records.');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const res = await api().post('/admin/users/bulk-import', { users });
      setResult(res.data);
      toast.success(`Imported ${res.data.inserted_count} / skipped ${res.data.skipped_count} / errors ${res.data.error_count}`);
      if (res.data.inserted_count > 0 && onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Import failed';
      toast.error(msg);
    }
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    setRaw('');
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl bg-card border-border" data-testid="bulk-import-users-modal">
        <DialogHeader>
          <DialogTitle className="text-white font-heading uppercase tracking-wide">Bulk Import Users</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Paste a JSON array or multiple JSON user records. Passwords must already be bcrypt hashes.
            Duplicate emails are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={`[\n  {\n    "id": "...",\n    "email": "user@example.com",\n    "name": "Jane Doe",\n    "password": "$2b$12$...",\n    "created_at": "2026-04-20T..."\n  }\n]`}
            className="min-h-[260px] font-mono text-xs bg-black border-border text-white"
            disabled={loading}
            data-testid="bulk-import-textarea"
          />

          {result && (
            <div className="bg-black/50 border border-border rounded p-3 text-sm space-y-2">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                <span>Inserted: <strong>{result.inserted_count}</strong></span>
              </div>
              <div className="text-muted-foreground">
                Skipped (already exist): <strong>{result.skipped_count}</strong>
              </div>
              {result.error_count > 0 && (
                <div className="flex items-start gap-2 text-red-400">
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <div>
                    <div>Errors: <strong>{result.error_count}</strong></div>
                    <ul className="mt-1 text-xs list-disc list-inside">
                      {result.errors.slice(0, 5).map((e, i) => (
                        <li key={`err-${e.index ?? i}-${e.email || ''}`}>{e.email || `row ${e.index}`}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div className="text-muted-foreground text-xs">
                Total users in DB: {result.total_users_in_db}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              onClick={handleClose}
              variant="outline"
              disabled={loading}
              data-testid="bulk-import-cancel-btn"
            >
              Close
            </Button>
            <Button
              onClick={handleImport}
              disabled={loading || !raw.trim()}
              className="bg-primary text-black hover:bg-primary/90"
              data-testid="bulk-import-submit-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Importing...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Import Users</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
